import type { ElementType, SVGProps } from 'react';

/**
 * Default edge of a sidebar nav icon, in px. The design set is drawn on a
 * 24-unit grid and rendered at 20px inside a 22px hitbox on the dark menu.
 */
export const NAV_ICON_SIZE = 20;

/**
 * Stroke weight baked into the set — part of the icons' identity, not a knob.
 * At 20px it draws the same 1.4px line the set had at 22px, a touch heavier in
 * grid units so the glyphs hold up as light strokes on the dark menu.
 */
export const NAV_ICON_STROKE_WIDTH = 1.7;

/**
 * A menu row's icon, held at the set's size so a flex row cannot squeeze it.
 * Takes any icon of the set; iconoir glyphs drawn beside them in the menu take
 * `NAV_ICON_SIZE` and `NAV_ICON_STROKE_WIDTH` directly.
 */
export const navIconNode = (Icon: ElementType) => (
  <Icon style={{ minWidth: NAV_ICON_SIZE, minHeight: NAV_ICON_SIZE }} />
);

export type NavIconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'fill' | 'stroke'>;

/**
 * Shared shell for the bespoke sidebar icon set: every icon in `./index.tsx` is
 * this `<svg>` plus its own geometry. The stroke attributes are fixed here
 * rather than repeated per icon so the whole family stays visually consistent —
 * `currentColor` lets each nav row drive the colour through CSS inheritance,
 * which is also why none of these icons carry a hard-coded hex.
 *
 * `width` / `height` stay overridable; the main nav and the Settings panel both
 * render at the default size.
 */
const NavIconBase = ({
  width = NAV_ICON_SIZE,
  height = NAV_ICON_SIZE,
  children,
  ...props
}: NavIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={NAV_ICON_STROKE_WIDTH}
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeMiterlimit={10}
    {...props}
  >
    {children}
  </svg>
);

export default NavIconBase;
